/**
 * MultipleChoiceOptions — generic answer button grid.
 *
 * Used for interval recognition, chord quality, cadence type, etc. The exercise
 * generator's Exercise.options array maps directly to the buttons prop.
 */

import { type ReactNode } from "react";

export interface MCOption {
  id: string;
  label: string;
  sublabel?: string;
}

export interface MultipleChoiceOptionsProps {
  options: MCOption[];
  selectedId?: string;
  correctId?: string;
  /** When true, options are non-clickable and styled to show feedback. */
  graded?: boolean;
  disabled?: boolean;
  onSelect: (id: string) => void;
  /** Layout columns; defaults based on option count. */
  columns?: 2 | 3 | 4 | 5;
}

export function MultipleChoiceOptions({
  options,
  selectedId,
  correctId,
  graded = false,
  disabled = false,
  onSelect,
  columns,
}: MultipleChoiceOptionsProps): ReactNode {
  const cols = columns ?? defaultColumns(options.length);
  return (
    <div className={`grid gap-2 ${gridColsClass(cols)}`}>
      {options.map((opt) => (
        <button
          key={opt.id}
          type="button"
          disabled={disabled}
          onClick={() => onSelect(opt.id)}
          aria-pressed={!graded && selectedId === opt.id ? true : undefined}
          aria-label={buildAriaLabel(opt, selectedId, correctId, graded)}
          className={getOptionClass(opt.id, selectedId, correctId, graded)}
        >
          <span className="block font-medium">{opt.label}</span>
          {opt.sublabel && (
            <span className="block font-mono text-xs mt-1 opacity-70">{opt.sublabel}</span>
          )}
        </button>
      ))}
    </div>
  );
}

function defaultColumns(n: number): 2 | 3 | 4 | 5 {
  if (n <= 3) return 3;
  if (n === 4) return 4;
  return 5;
}

function gridColsClass(n: 2 | 3 | 4 | 5): string {
  return { 2: "grid-cols-2", 3: "grid-cols-3", 4: "grid-cols-4", 5: "grid-cols-5" }[n];
}

/**
 * Compose an aria-label that surfaces correctness during graded state.
 * Visual cues (color, line-through) on their own would leave color-blind
 * and screen-reader users without the most important piece of info.
 */
function buildAriaLabel(
  opt: MCOption,
  selectedId: string | undefined,
  correctId: string | undefined,
  graded: boolean,
): string {
  const base = opt.sublabel ? `${opt.label}, ${opt.sublabel}` : opt.label;
  if (!graded) return base;
  if (opt.id === correctId) return `${base} (correct answer)`;
  if (opt.id === selectedId) return `${base} (your selection — incorrect)`;
  return base;
}

function getOptionClass(
  id: string,
  selectedId: string | undefined,
  correctId: string | undefined,
  graded: boolean,
): string {
  const shared = "py-3 px-3 border transition-colors text-sm";
  if (graded && id === correctId) return `${shared} bg-success/10 border-success text-success`;
  if (graded && id === selectedId && id !== correctId) return `${shared} bg-danger/10 border-danger text-danger`;
  const interactive = `${shared} border-ink/20 disabled:opacity-40 disabled:cursor-not-allowed`;
  if (selectedId === id) return `${interactive} bg-paperWarm`;
  return `${interactive} bg-paper hover:bg-ink hover:text-paper hover:border-ink`;
}
